import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { useAuth } from '../../../context/AuthContext';
import Spinner from '../../../components/common/Spinner';

const TABLE_HEADERS = ['일시', '회원명', '중개사무소명', '추천 유효기한'];

const fmt = (ts) => {
  if (!ts) return '-';
  let d;
  if (ts.toDate) d = ts.toDate();
  else if (ts.seconds) d = new Date(ts.seconds * 1000);
  else d = new Date(ts);
  if (isNaN(d.getTime())) return '-';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const ReferralMembers = () => {
  const { userData } = useAuth();
  const [stats, setStats] = useState({ sent: 0, joined: 0, expired: 0 });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userData?.uid) return;
    setLoading(true);
    (async () => {
      try {
        // 1) realtor_invitations (발송/초대 기록)
        let sent = 0;
        try {
          const invSnap = await getDocs(query(
            collection(db, 'realtor_invitations'),
            where('realtor_id', '==', userData.uid)
          ));
          sent = invSnap.size;
        } catch {}

        // 2) realtors where referrer_id == me (내가 추천해서 가입한 중개사)
        let joinedRows = [];
        let joined = 0;
        let expired = 0;
        try {
          const rSnap = await getDocs(query(
            collection(db, 'realtors'),
            where('referrer_id', '==', userData.uid)
          ));
          for (const d of rSnap.docs) {
            const r = d.data();
            // 추천 유효기간 1년
            const createdDate = r.createdAt?.toDate?.() || (r.createdAt?.seconds ? new Date(r.createdAt.seconds * 1000) : null);
            const expireDate = createdDate ? new Date(createdDate.getTime() + 365 * 24 * 60 * 60 * 1000) : null;
            const isExpired = expireDate ? Date.now() > expireDate.getTime() : false;
            if (isExpired) expired += 1; else joined += 1;
            joinedRows.push({
              id: d.id,
              date: fmt(r.createdAt),
              name: r.businessName || r.business_name || d.id,
              office: r.officeName || r.office_name || r.businessName || '-',
              expire: fmt(expireDate),
            });
          }
        } catch {}

        setStats({ sent: Math.max(sent, joined), joined, expired });
        setRows(joinedRows);
      } catch (e) { /* skip */ }
      finally { setLoading(false); }
    })();
  }, [userData?.uid]);

  if (loading) return <Spinner fullPage={false} />;

  const STATS_DISPLAY = [
    { label: '총 발송 횟수', value: stats.sent },
    { label: '추천인 가입', value: stats.joined },
    { label: '추천인 리워드 종료', value: stats.expired },
  ];

  return (
    <Container>
      {/* 상단 통계 */}
      <StatsRow>
        {STATS_DISPLAY.map((s, i) => (
          <StatCell key={i} $last={i === STATS_DISPLAY.length - 1}>
            <StatLabel>{s.label}</StatLabel>
            <StatValue>{s.value}</StatValue>
          </StatCell>
        ))}
      </StatsRow>

      {/* 테이블 헤더 */}
      <TableHeader>
        {TABLE_HEADERS.map((h) => (
          <ThCell key={h}>{h}</ThCell>
        ))}
      </TableHeader>

      {rows.length === 0 ? (
        <EmptyBox>
          <EmptyIcon>
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
              <circle cx="20" cy="16" r="8" stroke="#D1D5DB" strokeWidth="2" fill="none" />
              <path d="M6 34c0-7.732 6.268-14 14-14s14 6.268 14 14" stroke="#D1D5DB" strokeWidth="2" fill="none" />
            </svg>
          </EmptyIcon>
          <EmptyText>표시할 추천 가입 회원이 없습니다.</EmptyText>
        </EmptyBox>
      ) : (
        rows.map((r) => (
          <TableRowDiv key={r.id}>
            <TdCell>{r.date}</TdCell>
            <TdCell>{r.name}</TdCell>
            <TdCell>{r.office}</TdCell>
            <TdCell>{r.expire}</TdCell>
          </TableRowDiv>
        ))
      )}
    </Container>
  );
};

export default ReferralMembers;

// ───── 스타일 ─────

const Container = styled.div`
  padding-bottom: ${({ theme }) => theme.spacing.lg};
`;

const StatsRow = styled.div`
  display: flex;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const StatCell = styled.div`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.base} ${({ theme }) => theme.spacing.sm};
  border-right: ${({ $last, theme }) => $last ? 'none' : `1px solid ${theme.colors.border}`};
`;

const StatLabel = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const StatValue = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
`;

const TableHeader = styled.div`
  display: flex;
  background: ${({ theme }) => theme.colors.borderLight};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const ThCell = styled.div`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: center;
`;

const EmptyBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.xxxl} 0;
`;

const EmptyIcon = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const EmptyText = styled.p`
  font-size: ${({ theme }) => theme.fontSize.base};
  color: ${({ theme }) => theme.colors.text};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
`;

const TableRowDiv = styled.div`
  display: flex;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const TdCell = styled.div`
  flex: 1;
  padding: 12px 8px;
  font-size: 13px;
  color: #1F2937;
  text-align: center;
`;
